import axios, { AxiosError } from "axios";

type LogLevel = "None" | "Trace" | "Debug" | "Information" | "Warning" | "Error" | "Critical";

const LOGGER_ENDPOINT = "https://logger.api.szoftweb.com/log/nojwt";
const LOGGER_API_KEY = (process.env.REACT_APP_LOGGER_API_KEY ?? "").trim();
const PROGRAM_NAME = (process.env.REACT_APP_LOGGER_PROGRAM_NAME ?? "micra_react").trim();
const PROGRAM_VERSION = (process.env.REACT_APP_LOGGER_PROGRAM_VERSION ?? "v1.0.0.0").trim();
const UNKNOWN_ERROR_MESSAGE = "Ismeretlen hiba történt...";

declare global {
  interface Window {
    micraLoggerTest?: (message?: string, subMessage?: string) => Promise<boolean>;
  }

  interface Navigator {
    connection?: {
      downlink?: number;
      effectiveType?: string;
      rtt?: number;
      saveData?: boolean;
      type?: string;
    };
  }
}

type LoggerPayload = {
  apiKey: string;
  addData: {
    programName: string;
    logLevelName: LogLevel;
    version: string;
    message: string;
    subMessage: string;
  };
};

type DiagnosticEvent = {
  timestamp: string;
  event: string;
  details?: Record<string, unknown>;
};

const MAX_DIAGNOSTIC_EVENTS = 25;
let diagnosticsInitialized = false;
const recentDiagnosticEvents: DiagnosticEvent[] = [];

const addDiagnosticEvent = (event: string, details?: Record<string, unknown>): void => {
  recentDiagnosticEvents.push({
    timestamp: new Date().toISOString(),
    event,
    details,
  });

  if (recentDiagnosticEvents.length > MAX_DIAGNOSTIC_EVENTS) {
    recentDiagnosticEvents.splice(0, recentDiagnosticEvents.length - MAX_DIAGNOSTIC_EVENTS);
  }
};

const getConnectionInfo = (): Record<string, unknown> | null => {
  const connection = navigator.connection;
  if (!connection) {
    return null;
  }

  return {
    type: connection.type ?? null,
    effectiveType: connection.effectiveType ?? null,
    downlink: connection.downlink ?? null,
    rtt: connection.rtt ?? null,
    saveData: connection.saveData ?? null,
  };
};

const getNavigationInfo = (): Record<string, unknown> | null => {
  const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (!navigationEntry) {
    return null;
  }

  return {
    type: navigationEntry.type,
    domComplete: Math.round(navigationEntry.domComplete),
    loadEventEnd: Math.round(navigationEntry.loadEventEnd),
  };
};

export const initLoggerDiagnostics = (): void => {
  if (diagnosticsInitialized || typeof window === "undefined") {
    return;
  }

  diagnosticsInitialized = true;

  addDiagnosticEvent("logger_diagnostics_initialized", {
    href: window.location.href,
    online: navigator.onLine,
    visibilityState: document.visibilityState,
  });

  window.addEventListener("online", (): void => {
    addDiagnosticEvent("browser_online", {
      online: navigator.onLine,
    });
  });

  window.addEventListener("offline", (): void => {
    addDiagnosticEvent("browser_offline", {
      online: navigator.onLine,
    });
  });

  window.addEventListener("focus", (): void => {
    addDiagnosticEvent("window_focus");
  });

  window.addEventListener("blur", (): void => {
    addDiagnosticEvent("window_blur");
  });

  window.addEventListener("beforeunload", (): void => {
    addDiagnosticEvent("beforeunload");
  });

  window.addEventListener("pagehide", (event: PageTransitionEvent): void => {
    addDiagnosticEvent("pagehide", {
      persisted: event.persisted,
    });
  });

  window.addEventListener("pageshow", (event: PageTransitionEvent): void => {
    addDiagnosticEvent("pageshow", {
      persisted: event.persisted,
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent): void => {
    addDiagnosticEvent("unhandledrejection", {
      reason:
        event.reason instanceof Error
          ? {
              name: event.reason.name,
              message: event.reason.message,
            }
          : { value: typeof event.reason === "string" ? event.reason : "non-error rejection" },
    });
  });

  window.addEventListener("error", (event: ErrorEvent): void => {
    addDiagnosticEvent("window_error", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  document.addEventListener("visibilitychange", (): void => {
    addDiagnosticEvent("visibilitychange", {
      visibilityState: document.visibilityState,
    });
  });
};

export const getLoggerDiagnosticsSnapshot = (): Record<string, unknown> => {
  return {
    location: window.location.href,
    online: navigator.onLine,
    visibilityState: document.visibilityState,
    hasFocus: document.hasFocus(),
    connection: getConnectionInfo(),
    navigation: getNavigationInfo(),
    recentEvents: [...recentDiagnosticEvents],
  };
};

const truncate = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)} ...[truncated]`;
};

const safeSerialize = (input: unknown): string => {
  const seen = new WeakSet<object>();

  return truncate(
    JSON.stringify(
      input,
      (_key: string, value: unknown) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) {
            return "[Circular]";
          }
          seen.add(value);
        }
        if (typeof value === "function") {
          return "[Function]";
        }
        if (typeof value === "bigint") {
          return value.toString();
        }
        return value;
      },
      2,
    ) ?? "",
    12000,
  );
};

const redactHeaders = (headers: unknown): Record<string, unknown> | undefined => {
  if (!headers || typeof headers !== "object") {
    return undefined;
  }

  return Object.entries(headers as Record<string, unknown>).reduce(
    (accumulator: Record<string, unknown>, [key, value]: [string, unknown]): Record<string, unknown> => {
      const normalizedKey = key.toLowerCase();
      accumulator[key] = ["authorization", "cookie", "set-cookie"].includes(normalizedKey) ? "[REDACTED]" : value;
      return accumulator;
    },
    {},
  );
};

const extractErrorContext = (error: unknown): Record<string, unknown> => {
  if (!error) {
    return {};
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return {
      type: "AxiosError",
      message: axiosError.message,
      code: axiosError.code,
      stack: axiosError.stack,
      request: {
        method: axiosError.config?.method,
        url: axiosError.config?.url,
        baseURL: axiosError.config?.baseURL,
        timeout: axiosError.config?.timeout,
        headers: redactHeaders(axiosError.config?.headers),
      },
      response: {
        status: axiosError.response?.status,
        statusText: axiosError.response?.statusText,
        data: axiosError.response?.data,
      },
      transport: {
        readyState: (axiosError.request as XMLHttpRequest | undefined)?.readyState,
        status: (axiosError.request as XMLHttpRequest | undefined)?.status,
        responseURL: (axiosError.request as XMLHttpRequest | undefined)?.responseURL,
        timeout: (axiosError.request as XMLHttpRequest | undefined)?.timeout,
      },
    };
  }

  if (error instanceof Error) {
    return {
      type: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  return {
    type: typeof error,
    value: error,
  };
};

export const logWriteToLogger = async (
  apiKey: string,
  message: string,
  subMessage: string = "",
  logLevel: LogLevel = "Error",
  programName: string = PROGRAM_NAME,
  version: string = PROGRAM_VERSION,
): Promise<boolean> => {
  if (!apiKey) {
    return false;
  }

  const payload: LoggerPayload = {
    apiKey,
    addData: {
      programName,
      logLevelName: logLevel,
      version,
      message,
      subMessage,
    },
  };

  try {
    await axios.post(LOGGER_ENDPOINT, payload, {
      headers: {
        Accept: "application/json",
        "Content-Type": "text/json; charset=UTF-8",
      },
      timeout: 5000,
    });
    return true;
  } catch {
    // Best effort logging only. Never block the app with logger failures.
    return false;
  }
};

export const sendLoggerTestCall = async (
  message: string = "Manual logger API test from micra_react",
  subMessage: string = "Triggered from development helper",
): Promise<boolean> => {
  return logWriteToLogger(LOGGER_API_KEY, message, subMessage, "Information");
};

export const logUnknownError = (context: string, error?: unknown, extra?: Record<string, unknown>): void => {
  const subMessage = safeSerialize({
    context,
    timestamp: new Date().toISOString(),
    location: window.location.href,
    userAgent: navigator.userAgent,
    browser: getLoggerDiagnosticsSnapshot(),
    error: extractErrorContext(error),
    extra: extra ?? null,
  });

  void logWriteToLogger(LOGGER_API_KEY, `Unknown error in ${context}`, subMessage, "Error");
};

if (process.env.NODE_ENV !== "production") {
  window.micraLoggerTest = async (message?: string, subMessage?: string): Promise<boolean> => {
    return sendLoggerTestCall(message, subMessage);
  };
}

export { UNKNOWN_ERROR_MESSAGE };
